/* As an ngModel Directive
<mb-radio-group [(ngModel)]="selectedValue" [options]="options" (ngModelChange)="valueUpdated($event)">
</mb-radio-group>

valueUpdated(updatedValue) {
  console.log(updatedValue);
}
*/
/* As form control
<mb-radio-group formControlName="gender" [options]="genderOptions"></mb-radio-group>
this.form = this.fb.group({
  gender: [
    '',
    Validators.required
  ],
});
*/

/* to get selected object as field value
 <mb-radio-group formControlName="gender" [options]="genderOptions" [originalForm]="true"></mb-radio-group>
 */
// Why are we using ngDefaultControl?
//  https://stackoverflow.com/questions/46465891/what-is-ngdefaultcontrol-in-angular/46465959

/* Oval shape radio buttons
  - To create oval shape radio button, pass the radioStyle property to the component
*/

import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, Input, Optional, forwardRef } from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { IonicModule } from '@ionic/angular';

export interface IOptions {
  name: string;
  id?: string | boolean;
  value?: string | number | boolean;
  dataTestId?: string;
  ariaLabel?: string;
}
@Component({
  selector: 'mb-radio-group',
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule],
  templateUrl: './radio-group.component.html',
  styleUrls: ['./radio-group.component.scss'],
  providers: [
    {
      /*eslint-disable */
      // to fix @typescript-eslint/no-use-before-define
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioGroupComponent),
      multi: true,
      /* eslint-enable */
    },
  ],
})
export class RadioGroupComponent implements ControlValueAccessor, AfterContentInit {
  @Input() formControl: UntypedFormControl;

  @Input() formControlName: string;

  /**
   *  get hold of FormControl instance no matter formControl or formControlName is given.
   * If formControlName is given, then this.controlContainer.control is the parent FormGroup (or FormArray) instance.
   */
  get control() {
    return this.formControl || (this.controlContainer && this.controlContainer.control.get(this.formControlName));
  }

  set value(val) {
    // this value is updated by programmatic changes
    if (val !== undefined && this.val !== val) {
      this.val = val;
      this.onChange(val);
      this.onTouch();
    }
  }

  @Input() disabled = false;

  @Input() valueField = 'id';

  @Input() originalForm = false;

  @Input() options: Array<IOptions> = [];

  @Input() size = 'small';

  @Input() radioStyle: 'oval' | 'oval-grid' | 'default-grid' | 'default' = 'default';

  val: any; // this is the updated value that the class accesses

  // ControlContainer made optional for the component to work with ngModel.
  constructor(@Optional() private controlContainer: ControlContainer) {}

  /**
   * Determines if selected style is of oval type.
   */
  get isOvalStyle() {
    return this.radioStyle.includes('oval');
  }

  /**
   * Reset control value if the preselected value doesn't exist in options array.
   */
  ngAfterContentInit() {
    // Reset control value if the preselected value doesn't exist in options array - for example in the case that a previously available option is no longer available.
    if (this.control && !this.existInOptions(this.control.value)) {
      this.control.setValue(null);
      this.control.markAsUntouched();
      this.control.markAsPristine();
    }
  }

  /**
   * Checks if the value exists in options.
   *
   * @param val - Selected value.
   */
  existInOptions(val) {
    return !!this.options.filter((s) =>
      this.originalForm ? val && s[this.valueField] === val[this.valueField] : s[this.valueField] === val,
    ).length;
  }

  // Function to call when the selected options change.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: any) => {
    // console.log(value);
  };

  //   // Function to call when the input is touched (when a star is clicked).
  onTouch = () => {};

  // this method sets the value programmatically
  writeValue(value: any) {
    this.value = value;
  }

  // upon UI element value changes, this method gets triggered
  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  // upon touching the element, this method gets triggered
  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  onOptionSwitch(event: CustomEvent) {
    event.preventDefault();
    if (this.originalForm) {
      this.writeValue(this.options.filter((o) => o[this.valueField] === event.detail.value)[0]);
    } else {
      this.writeValue(event.detail.value);
    }
  }

  selectedValue(): boolean {
    if (this.val === undefined || this.val === null) {
      return;
    }

    return this.originalForm ? this.val[this.valueField] : this.val;
  }
}
